import { useMemo } from "react";

import type { ThreadWithMessages } from "@/components/organisms/Thread/Thread.types";
import { PendingNode } from "./PendingSession.types";

/** A thread's change rows as the records they touched, for listing. */
export function buildPendingNodes(
  changes: NonNullable<ThreadWithMessages["pendingSession"]>["changes"],
): PendingNode[] {
  const pendingNodes: PendingNode[] = [];
  for (const change of changes ?? []) {
    const nodeAny = change.node as {
      id?: string;
      name?: string;
      description?: string;
      __typename?: string;
    } & Record<string, unknown>;
    pendingNodes.push({
      node: {
        // Its kind's title fields as read: a row is titled by them, unread.
        ...nodeAny,
        id:
          nodeAny?.id ||
          btoa(`${change.modelName}Object:${change.objectIdentifier}`),
        identifier: change.objectIdentifier,
        __typename:
          nodeAny?.__typename ||
          `${change.modelName?.charAt(0).toUpperCase()}${change.modelName?.slice(1)}Object`,
        name: nodeAny?.name || nodeAny?.description || change.objectIdentifier,
        entity: change.objectEntity,
      },
      changeType: change.changeType || "update",
      modelName: change.modelName || "unknown",
      status: (change as { status?: string }).status || "pending",
      changeIdentifier: change.identifier,
      parentIdentifier:
        (change as { parent?: { identifier?: string } }).parent?.identifier ??
        null,
    });
  }
  return pendingNodes;
}

export function usePendingChanges(
  pendingSession: ThreadWithMessages["pendingSession"] | null | undefined,
) {
  return useMemo(() => {
    const sessionOpen =
      pendingSession?.status === "pending" &&
      pendingSession.hasPendingChanges !== false;

    if (!sessionOpen || !pendingSession?.identifier) {
      return { pendingNodes: [], pendingSessionId: null };
    }

    return {
      pendingNodes: buildPendingNodes(
        (pendingSession.changes ?? []).filter(
          (change) => change.status == null || change.status === "pending",
        ),
      ),
      pendingSessionId: pendingSession.identifier,
    };
  }, [pendingSession]);
}
